import { useState } from "react";

const ADDRESS_COPY = "realEstate.casaMapa.admin.listings.detail.address";

function formatSnapMeters(value) {
  const meters = Number(value);
  if (!Number.isFinite(meters)) {
    return null;
  }
  return meters < 10 ? meters.toFixed(1) : String(Math.round(meters));
}

function confidenceTone(confidence) {
  const normalized = String(confidence || "").toLowerCase();
  if (normalized === "high") return "high";
  if (normalized === "medium") return "medium";
  if (normalized === "low") return "low";
  if (normalized === "none") return "none";
  return null;
}

function methodLabel(method, t) {
  if (!method) return null;
  const key = `${ADDRESS_COPY}.methods.${method}`;
  const translated = t(key);
  return translated === key ? method.replace(/_/g, " ") : translated;
}

export default function CasaMapaAdminListingAddress({
  mailingAddress,
  t,
  onTogglePublicStreet,
  togglingPublicStreet = false,
}) {
  const [localError, setLocalError] = useState("");

  const officialLine = mailingAddress?.official ? mailingAddress.mailingLine : null;
  const sourceLine = mailingAddress?.parsedRaw || null;
  const fallbackLine =
    !officialLine && mailingAddress?.mailingLine ? mailingAddress.mailingLine : null;
  const showSource = Boolean(officialLine && sourceLine && sourceLine !== officialLine);
  const published = Boolean(mailingAddress?.publicStreetApproved);
  const confidence = confidenceTone(mailingAddress?.addressConfidence);
  const snapMeters = formatSnapMeters(mailingAddress?.snapDistanceM);
  const method = methodLabel(mailingAddress?.addressMethod, t);
  const canToggle = typeof onTogglePublicStreet === "function" && Boolean(mailingAddress);
  const hasAnyLine = Boolean(officialLine || sourceLine || fallbackLine);

  async function handleToggle() {
    if (!canToggle) return;
    setLocalError("");
    try {
      await onTogglePublicStreet(!published);
    } catch (error) {
      setLocalError(error?.message || t(`${ADDRESS_COPY}.publishError`));
    }
  }

  return (
    <section className="cm-listing-address" aria-label={t(`${ADDRESS_COPY}.title`)}>
      <div className="cm-listing-address__header">
        <h3 className="cm-listing-address__title">{t(`${ADDRESS_COPY}.title`)}</h3>
        {mailingAddress ? (
          <span
            className={`cm-listing-address__badge${published ? " cm-listing-address__badge--published" : ""}`}
          >
            {published ? t(`${ADDRESS_COPY}.publishedPublic`) : t(`${ADDRESS_COPY}.hiddenPublic`)}
          </span>
        ) : (
          <span className="cm-listing-address__badge cm-listing-address__badge--missing">
            {t(`${ADDRESS_COPY}.missingBadge`)}
          </span>
        )}
        {confidence ? (
          <span
            className={`cm-listing-address__confidence cm-listing-address__confidence--${confidence}`}
          >
            {t(`${ADDRESS_COPY}.confidenceLabel`, {
              level: t(`${ADDRESS_COPY}.confidence.${confidence}`),
            })}
          </span>
        ) : null}
      </div>

      {hasAnyLine ? (
        <p className="cm-listing-address__line">{officialLine || fallbackLine || sourceLine}</p>
      ) : (
        <p className="cm-listing-address__line cm-listing-address__line--empty">
          {t(`${ADDRESS_COPY}.empty`)}
        </p>
      )}

      {officialLine && showSource ? (
        <p className="cm-listing-address__source">
          <span>{t(`${ADDRESS_COPY}.source`)}</span> {sourceLine}
        </p>
      ) : null}

      {mailingAddress?.doNotMail ? (
        <p className="cm-listing-address__note">{t(`${ADDRESS_COPY}.doNotMail`)}</p>
      ) : null}

      {mailingAddress?.addressMethod === "needs_review" ? (
        <p className="cm-listing-address__note">{t(`${ADDRESS_COPY}.needsReview`)}</p>
      ) : mailingAddress && !officialLine && hasAnyLine ? (
        <p className="cm-listing-address__note">{t(`${ADDRESS_COPY}.notSnapped`)}</p>
      ) : null}

      {mailingAddress ? (
        <dl className="cm-listing-address__facts">
          {confidence ? (
            <div>
              <dt>{t(`${ADDRESS_COPY}.confidenceTitle`)}</dt>
              <dd>
                <span
                  className={`cm-listing-address__confidence cm-listing-address__confidence--${confidence}`}
                >
                  {t(`${ADDRESS_COPY}.confidence.${confidence}`)}
                </span>
              </dd>
            </div>
          ) : null}
          {mailingAddress.addressClass ? (
            <div>
              <dt>{t(`${ADDRESS_COPY}.classLabel`)}</dt>
              <dd>{t(`${ADDRESS_COPY}.class`, { class: mailingAddress.addressClass })}</dd>
            </div>
          ) : null}
          {method ? (
            <div>
              <dt>{t(`${ADDRESS_COPY}.methodLabel`)}</dt>
              <dd>{method}</dd>
            </div>
          ) : null}
          {snapMeters ? (
            <div>
              <dt>{t(`${ADDRESS_COPY}.snapLabel`)}</dt>
              <dd>{t(`${ADDRESS_COPY}.snap`, { meters: snapMeters })}</dd>
            </div>
          ) : null}
          {mailingAddress.postalCode ? (
            <div>
              <dt>{t(`${ADDRESS_COPY}.postalLabel`)}</dt>
              <dd>{mailingAddress.postalCode}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}

      {canToggle ? (
        <div className="cm-listing-address__publish">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={
              togglingPublicStreet ||
              (!published && !mailingAddress.canApprovePublicStreet)
            }
            onClick={handleToggle}
          >
            {togglingPublicStreet
              ? t(`${ADDRESS_COPY}.publishSaving`)
              : published
                ? t(`${ADDRESS_COPY}.unpublishStreet`)
                : t(`${ADDRESS_COPY}.publishStreet`)}
          </button>
          {!published && !mailingAddress.canApprovePublicStreet ? (
            <p className="cm-listing-address__note">{t(`${ADDRESS_COPY}.publishBlocked`)}</p>
          ) : (
            <p className="muted cm-listing-address__note">{t(`${ADDRESS_COPY}.publishHint`)}</p>
          )}
          {localError ? (
            <p className="cm-listing-address__note" role="alert">
              {localError}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
